import { useState, useEffect, useRef } from "react";
import {
  collection, query, where, onSnapshot,
  addDoc, serverTimestamp, deleteDoc, doc, getDocs
} from "firebase/firestore";
import { db } from "../firebase";
import Icon from "./Icon";
import NewTripModal from "./NewTripModal";
import SearchModal from "./SearchModal";
import { formatDate, TRIP_COLORS, localDateStr, isUpcoming } from "../utils";

// A trip is shared when more than one person is a member
const isShared = (t) => (t?.memberIds?.length || 1) > 1;

// Next upcoming segment for a trip, judged against the current time.
// Uses the stored upcomingSegments list, falling back to the older nextSegment field.
const nextUpcoming = (trip, now) =>
  (trip.upcomingSegments || (trip.nextSegment ? [trip.nextSegment] : []))
    .find((s) => isUpcoming(s, now));

const SEGMENT_ICONS = {
  flight: "✈️", hotel: "🏨", restaurant: "🍽️",
  activity: "🎯", car: "🚗", train: "🚆", other: "📌",
};

export default function TripsList({ user, onSelectTrip, onStats, onRestaurants, onPacking, onSettings, onImport, savedScrollY, isOnline }) {
  const [trips, setTrips] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeSelected, setMergeSelected] = useState(new Set());
  const [mergeTarget, setMergeTarget] = useState(null);
  const [merging, setMerging] = useState(false);
  const [expandedPreview, setExpandedPreview] = useState(null);
  const [previewSegments, setPreviewSegments] = useState({});
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Current time, refreshed every minute so "Next up" advances on its own
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);
  const today = localDateStr(now);

  useEffect(() => {
    const q = query(
      collection(db, "trips"),
      where("memberIds", "array-contains", user.uid)
    );
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
      setTrips(data);
      setLoading(false);
      if (savedScrollY?.current > 0) {
        requestAnimationFrame(() => window.scrollTo(0, savedScrollY.current));
      }
    });
    return unsub;
  }, [user.uid, savedScrollY]);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleCreateTrip = async (tripData) => {
    const ref = await addDoc(collection(db, "trips"), {
      ...tripData,
      memberIds: [user.uid],
      members: [user.email],
      createdBy: user.uid,
      createdAt: serverTimestamp(),
    });
    onSelectTrip({ id: ref.id, ...tripData, memberIds: [user.uid], members: [user.email] });
  };

  const futureTrips = trips.filter(t => !t.endDate || t.endDate >= today);
  const pastTrips = trips
    .filter(t => t.endDate && t.endDate < today)
    .sort((a, b) => (b.startDate || "").localeCompare(a.startDate || ""));

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(trips.map(t => t.id)));
  const selectAllPast = () => setSelected(new Set(pastTrips.map(t => t.id)));
  const clearSelection = () => setSelected(new Set());

  const exitDeleteMode = () => {
    setDeleteMode(false);
    setSelected(new Set());
    setExpandedPreview(null);
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} trip${selected.size !== 1 ? "s" : ""}? This cannot be undone.`)) return;
    setDeleting(true);
    for (const id of selected) {
      await deleteDoc(doc(db, "trips", id));
    }
    setSelected(new Set());
    setDeleting(false);
    exitDeleteMode();
  };

  const toggleMergeSelect = (id) => {
    const next = new Set(mergeSelected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setMergeSelected(next);
    // A shared trip must always be the one that is kept
    const shared = [...next].filter(i => isShared(trips.find(t => t.id === i)));
    setMergeTarget(cur => {
      if (shared.length === 1) return shared[0];
      if (cur && next.has(cur) && shared.length === 0) return cur;
      return null;
    });
  };

  const handleMerge = async () => {
    if (!mergeTarget || mergeSelected.size < 2) return;
    const sources = [...mergeSelected].filter(id => id !== mergeTarget);
    const targetTrip = trips.find(t => t.id === mergeTarget);
    // Safety: never delete a shared trip as part of a merge
    if (sources.some(id => isShared(trips.find(t => t.id === id)))) {
      window.alert("A shared trip can't be merged into another trip. Choose the shared trip as the one to keep.");
      return;
    }
    const names = sources.map(id => `"${trips.find(t => t.id === id)?.name}"`).join(", ");
    if (!window.confirm(`Merge ${names} into "${targetTrip?.name}"?\n\n${names} will be DELETED and its segments moved into "${targetTrip?.name}".`)) return;
    setMerging(true);
    for (const sourceId of sources) {
      const segsSnap = await getDocs(collection(db, "trips", sourceId, "segments"));
      for (const segDoc of segsSnap.docs) {
        await addDoc(collection(db, "trips", mergeTarget, "segments"), {
          ...segDoc.data(),
          createdAt: serverTimestamp(),
          mergedFrom: sourceId,
        });
      }
      await deleteDoc(doc(db, "trips", sourceId));
    }
    setMerging(false);
    setShowMergeModal(false);
    setMergeSelected(new Set());
    setMergeTarget(null);
  };

  const mergePicked = [...mergeSelected].map(id => trips.find(x => x.id === id)).filter(Boolean);
  const mergeShared = mergePicked.filter(isShared);
  const mergeBlocked = mergeShared.length > 1; // can't absorb one shared trip into another here

  const loadPreview = async (tripId) => {
    if (previewSegments[tripId]) {
      setExpandedPreview(expandedPreview === tripId ? null : tripId);
      return;
    }
    const snap = await getDocs(collection(db, "trips", tripId, "segments"));
    const segs = snap.docs
      .map(d => d.data())
      .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
      .slice(0, 5);
    setPreviewSegments(prev => ({ ...prev, [tripId]: segs }));
    setExpandedPreview(tripId);
  };

  const daysUntilLabel = (trip) => {
    if (!trip.startDate) return null;
    const start = new Date(trip.startDate + "T00:00:00");
    const end = trip.endDate ? new Date(trip.endDate + "T00:00:00") : start;
    const now = new Date(today + "T00:00:00");
    const daysToStart = Math.round((start - now) / 86400000);
    const daysToEnd = Math.round((end - now) / 86400000);
    if (daysToStart > 0) return { label: `in ${daysToStart} day${daysToStart !== 1 ? "s" : ""}`, color: daysToStart <= 7 ? "var(--accent)" : "var(--ink-faint)" };
    if (daysToEnd >= 0) return { label: "in progress", color: "#2E7D32" };
    return null;
  };

  const TripCard = ({ trip }) => {
    const isSelected = selected.has(trip.id);
    const isExpanded = expandedPreview === trip.id;
    const preview = previewSegments[trip.id];
    const countdown = daysUntilLabel(trip);

    return (
      <div className="trip-card" style={{ flexDirection: "column", alignItems: "stretch", padding: 0, overflow: "hidden" }}>
        <div
          style={{ display: "flex", alignItems: "center", padding: "14px 14px 14px 0" }}
          onClick={() => {
            if (deleteMode) toggleSelect(trip.id);
            else onSelectTrip(trip);
          }}
        >
          {deleteMode ? (
            <div style={{ padding: "0 12px", flexShrink: 0 }}>
              <div style={{
                width: 22, height: 22, borderRadius: "50%",
                border: `2px solid ${isSelected ? "var(--accent)" : "var(--border)"}`,
                background: isSelected ? "var(--accent)" : "white",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {isSelected && <span style={{ color: "white", fontSize: 13, lineHeight: 1 }}>✓</span>}
              </div>
            </div>
          ) : (
            <div
              className="trip-color-bar"
              style={{ background: trip.color || TRIP_COLORS[0], alignSelf: "stretch", width: 4, borderRadius: "4px 0 0 4px", marginRight: 14, flexShrink: 0 }}
            />
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {trip.name}
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-light)" }}>
              {trip.startDate && trip.endDate
                ? `${formatDate(trip.startDate)} — ${formatDate(trip.endDate)}`
                : trip.startDate ? formatDate(trip.startDate) : "Dates TBD"}
            </div>
            {countdown && (
              <div style={{ fontSize: 11, fontWeight: 500, color: countdown.color, marginTop: 3 }}>
                {countdown.label}
              </div>
            )}
          </div>

          {deleteMode ? (
            <button
              onClick={(e) => { e.stopPropagation(); loadPreview(trip.id); }}
              style={{
                background: "none", border: "1px solid var(--border)", borderRadius: 12,
                fontSize: 11, color: "var(--ink-light)", cursor: "pointer",
                padding: "3px 10px", fontFamily: "var(--font-body)", marginRight: 8, flexShrink: 0,
              }}
            >
              {isExpanded ? "Hide" : "View"}
            </button>
          ) : (
            <div className="trip-card-arrow">
              <Icon name="chevronRight" size={16} />
            </div>
          )}
        </div>

        {isExpanded && (
          <div style={{ borderTop: "1px solid var(--border)", background: "var(--paper-warm)", padding: "10px 14px 12px" }}>
            {!preview ? (
              <div style={{ fontSize: 12, color: "var(--ink-light)" }}>Loading...</div>
            ) : preview.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--ink-light)" }}>No segments</div>
            ) : (
              <>
                {preview.map((seg, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 4 }}>
                    <span style={{ fontSize: 13 }}>{SEGMENT_ICONS[seg.type] || "📌"}</span>
                    <span style={{ fontSize: 12, color: "var(--ink)", flex: 1 }}>{seg.title}</span>
                    <span style={{ fontSize: 11, color: "var(--ink-faint)", flexShrink: 0 }}>{seg.date ? formatDate(seg.date) : ""}</span>
                  </div>
                ))}
                {preview.length === 5 && (
                  <div style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 4 }}>+ more segments not shown</div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <header className="header">
        <div className="header-logo">Gulliver Travels</div>
        <div className="header-actions">
          {deleteMode ? (
            <>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button onClick={selectAllPast} style={{ background: "none", border: "none", fontSize: 12, color: "var(--accent)", cursor: "pointer", fontFamily: "var(--font-body)" }}>
                  Past
                </button>
                <button onClick={selectAll} style={{ background: "none", border: "none", fontSize: 12, color: "var(--accent)", cursor: "pointer", fontFamily: "var(--font-body)" }}>
                  All
                </button>
                {selected.size > 0 && (
                  <button onClick={clearSelection} style={{ background: "none", border: "none", fontSize: 12, color: "var(--ink-light)", cursor: "pointer", fontFamily: "var(--font-body)" }}>
                    Clear
                  </button>
                )}
              </div>
              <button
                style={{ background: "none", border: "none", fontFamily: "var(--font-body)", fontSize: 14, color: "var(--ink-light)", cursor: "pointer", padding: "4px 8px" }}
                onClick={exitDeleteMode}
              >
                Done
              </button>
            </>
          ) : (
            <>
              <button className="icon-btn" onClick={() => setShowSearch(true)} title="Search">
                <Icon name="search" size={16} />
              </button>
              <div ref={menuRef} className="dropdown-wrapper">
                <button className="menu-btn" onClick={() => setShowMenu(!showMenu)}>•••</button>
                {showMenu && (
                  <div className="dropdown">
                    <button className="dropdown-item" onClick={() => { onStats(); setShowMenu(false); }}>
                      📊 Travel Stats
                    </button>
                    <button className="dropdown-item" onClick={() => { onRestaurants(); setShowMenu(false); }}>
                      <Icon name="faveBadge" size={14} />{" "}My Restaurants
                    </button>
                    <button className="dropdown-item" onClick={() => { onPacking(); setShowMenu(false); }}>
                      🧳 Packing Lists
                    </button>
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { setDeleteMode(true); setShowMenu(false); }}>
                      🗑 Select trips to delete
                    </button>
                    <button className="dropdown-item" onClick={() => { setMergeSelected(new Set()); setMergeTarget(null); setShowMergeModal(true); setShowMenu(false); }}>
                      🔀 Merge trips
                    </button>
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { onSettings(); setShowMenu(false); }}>
                      ⚙️ Settings
                    </button>
                    <div className="dropdown-divider" />
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px" }}>
                      {user.photoURL && (
                        <img src={user.photoURL} alt="avatar" style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0 }} />
                      )}
                      <div style={{ fontSize: 13, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {user.displayName || user.email}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </header>

      <main className="main">
        {loading ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: "var(--ink-light)", fontSize: 14 }}>Loading...</div>
        ) : trips.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">✈️</div>
            <h3>No trips yet</h3>
            <p>Create your first trip or import from TripIt in Settings.</p>
            <button className="new-trip-btn" style={{ marginTop: 16 }} onClick={() => setShowNew(true)}>
              <Icon name="plus" size={15} /> New trip
            </button>
          </div>
        ) : (
          <>
            {(() => {
              // "Next up" card — soonest upcoming or in-progress trip with a nextSegment stored
              const nextTrip = futureTrips.find(t => nextUpcoming(t, now));
              if (!nextTrip) return null;
              const seg = nextUpcoming(nextTrip, now);
              const daysTo = seg.date ? Math.round((new Date(seg.date + "T00:00:00") - new Date(today + "T00:00:00")) / 86400000) : null;
              const icon = { flight: "✈️", hotel: "🏨", restaurant: "🍽️", activity: "🎯", car: "🚗", train: "🚆" }[seg.type] || "📌";
              return (
                <div
                  onClick={() => onSelectTrip(nextTrip)}
                  style={{
                    background: "var(--accent)", color: "white", borderRadius: 12,
                    padding: "14px 16px", marginBottom: 16, cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.75, letterSpacing: "0.05em", marginBottom: 6, textTransform: "uppercase" }}>Next up</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 22 }}>{icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{seg.title}</div>
                      <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{nextTrip.name}{daysTo !== null ? ` · ${daysTo === 0 ? "today" : daysTo === 1 ? "tomorrow" : `in ${daysTo} days`}` : ""}</div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {futureTrips.length > 0 && (
              <>
                <div className="trips-header">
                  <div className="trips-title">Upcoming</div>
                </div>
                <div className="trips-grid">
                  {futureTrips.map((trip) => <TripCard key={trip.id} trip={trip} />)}
                </div>
              </>
            )}

            {!isOnline && (
              <div style={{
                margin: "16px 0",
                background: "#1A1A1A", color: "#FAFAF8",
                fontSize: 13, textAlign: "center",
                padding: "8px 16px", borderRadius: 8,
                fontFamily: "var(--font-body)",
              }}>
                ✈️ You're offline — changes will sync when reconnected
              </div>
            )}

            {!deleteMode && (
              <div style={{ marginTop: 16, marginBottom: 8 }}>
                <button className="new-trip-btn" onClick={() => setShowNew(true)}>
                  <Icon name="plus" size={15} /> New trip
                </button>
              </div>
            )}

            {pastTrips.length > 0 && (
              <>
                <div className="trips-header" style={{ marginTop: 16 }}>
                  <div className="trips-title" style={{ fontSize: 18, color: "var(--ink-mid)" }}>Past trips</div>
                  <div style={{ fontSize: 13, color: "var(--ink-faint)" }}>{pastTrips.length}</div>
                </div>
                <div className="trips-grid">
                  {pastTrips.map((trip) => <TripCard key={trip.id} trip={trip} />)}
                </div>
              </>
            )}
          </>
        )}
      </main>

      {/* Delete mode bottom bar */}
      {deleteMode && selected.size > 0 && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0, maxWidth: 600, margin: "0 auto",
          background: "var(--paper-card)", borderTop: "1px solid var(--border)",
          padding: "12px 16px", display: "flex", gap: 10, alignItems: "center",
        }}>
          <div style={{ flex: 1, fontSize: 14, color: "var(--ink)" }}>
            {selected.size} trip{selected.size !== 1 ? "s" : ""} selected
          </div>
          <button
            onClick={handleBulkDelete}
            disabled={deleting}
            style={{
              background: "#C05621", color: "white", border: "none",
              borderRadius: "var(--radius-sm)", padding: "10px 16px",
              fontFamily: "var(--font-body)", fontSize: 14, fontWeight: 500, cursor: "pointer",
            }}
          >
            {deleting ? "Deleting..." : `🗑 Delete ${selected.size}`}
          </button>
        </div>
      )}

      {/* Merge modal */}
      {showMergeModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowMergeModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">🔀 Merge trips</div>
              <button className="modal-close" onClick={() => setShowMergeModal(false)}>
                <Icon name="close" size={14} />
              </button>
            </div>
            <div style={{ fontSize: 13, color: "var(--ink-light)", marginBottom: 12, lineHeight: 1.5 }}>
              Select 2+ trips to merge, then choose which one keeps everything.
            </div>

            <div style={{ maxHeight: 240, overflowY: "auto", marginBottom: 16, border: "1px solid var(--border)", borderRadius: "var(--radius-sm)" }}>
              {trips.map((t, i) => {
                const isSel = mergeSelected.has(t.id);
                return (
                  <div
                    key={t.id}
                    onClick={() => toggleMergeSelect(t.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 12, padding: "11px 14px",
                      borderBottom: i < trips.length - 1 ? "1px solid var(--border)" : "none",
                      cursor: "pointer",
                      background: isSel ? "var(--paper-warm)" : "transparent",
                    }}
                  >
                    <div style={{
                      width: 18, height: 18, borderRadius: 3,
                      border: `1.5px solid ${isSel ? "var(--accent)" : "var(--border)"}`,
                      background: isSel ? "var(--accent)" : "white",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      {isSel && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: "var(--ink-faint)" }}>
                        {t.startDate ? formatDate(t.startDate) : "No date"}
                        {t.endDate && t.endDate !== t.startDate ? ` — ${formatDate(t.endDate)}` : ""}
                        {isShared(t) && <span style={{ marginLeft: 8, fontWeight: 600, color: "var(--accent)" }}>👥 Shared</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {mergeSelected.size >= 2 && (
              <>
                <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)", marginBottom: 8 }}>
                  Which trip keeps everything?
                </div>
                {mergeBlocked && (
                  <div style={{ fontSize: 12, color: "#C05621", background: "#FEF3EC", borderRadius: 8, padding: "8px 12px", marginBottom: 10, lineHeight: 1.5 }}>
                    You've selected more than one shared trip. Shared trips can't be merged away because other travelers would lose them. Deselect all but one.
                  </div>
                )}
                {!mergeBlocked && mergeShared.length === 1 && (
                  <div style={{ fontSize: 12, color: "var(--ink-mid)", background: "var(--paper-warm)", borderRadius: 8, padding: "8px 12px", marginBottom: 10, lineHeight: 1.5 }}>
                    👥 "{mergeShared[0].name}" is shared, so it is always the one kept. The other trip(s) will be merged into it.
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                  {mergePicked.map(t => {
                    const eligible = mergeShared.length === 0 || isShared(t);
                    const isKept = mergeTarget === t.id;
                    return (
                      <div
                        key={t.id}
                        onClick={() => eligible && !mergeBlocked && setMergeTarget(t.id)}
                        style={{
                          display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                          background: isKept ? "var(--paper-warm)" : "var(--paper-card)",
                          border: `1.5px solid ${isKept ? "var(--accent)" : "var(--border)"}`,
                          borderRadius: "var(--radius-sm)",
                          cursor: eligible && !mergeBlocked ? "pointer" : "default",
                          opacity: eligible ? 1 : 0.85,
                        }}
                      >
                        <div style={{
                          width: 18, height: 18, borderRadius: "50%",
                          border: `2px solid ${isKept ? "var(--accent)" : "var(--border)"}`,
                          background: isKept ? "var(--accent)" : "white",
                          flexShrink: 0,
                        }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{isShared(t) ? "👥 " : ""}{t.name}</div>
                          {mergeTarget && !mergeBlocked && (
                            <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2, color: isKept ? "#2E7D32" : "#C05621" }}>
                              {isKept ? "KEEPS EVERYTHING" : "WILL BE DELETED — segments move into the kept trip"}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <button
              className="submit-btn"
              style={{ background: (mergeSelected.size >= 2 && mergeTarget && !mergeBlocked) ? "var(--accent)" : "var(--border)" }}
              disabled={mergeSelected.size < 2 || !mergeTarget || mergeBlocked || merging}
              onClick={handleMerge}
            >
              {merging ? "Merging..." : mergeSelected.size >= 2 ? `Merge ${mergeSelected.size} trips` : "Select trips above"}
            </button>
          </div>
        </div>
      )}

      {showNew && <NewTripModal onClose={() => setShowNew(false)} onCreate={handleCreateTrip} />}
      {showSearch && <SearchModal user={user} onClose={() => setShowSearch(false)} onSelectTrip={onSelectTrip} />}
    </>
  );
}
